import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import './BottomSheetModal.scss';

/**
 * Modal that adapts to the viewport: a slide-up bottom sheet on mobile and a
 * centered dialog on desktop (>=768px). Closes on backdrop click, the close
 * button, or Escape.
 */
const BottomSheetModal = ({
  isOpen,
  onClose,
  children,
  className = '',
  title = 'Dialog',
  // Opts out of the built-in top-right close button for sheets whose design
  // puts the dismiss control inside their own header row (StoreLocatorSheet
  // follows Figma 11481-12416, which leads with it on the left). Those sheets
  // render their own; without this they would show two.
  hideCloseButton = false,
}) => {
  // Callers commonly pass an inline arrow, so keep the latest handler in a ref
  // and key the effect on `isOpen` alone — otherwise the body-overflow lock and
  // the keydown listener are torn down and re-attached on every parent render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    // Restore whatever was there rather than writing a literal. This sheet can
    // open from INSIDE the account drawer, which holds its own `overflow:
    // hidden` on <body> for as long as it is mounted and re-applies it only when
    // its own deps change (TrashieContainer.jsx, effect keyed on the two drawer
    // flags). Writing 'unset' on close therefore left the page BEHIND the
    // still-open drawer scrollable for the rest of the drawer session — the
    // drawer's own save/restore cannot defend against a third party clobbering
    // the property while it is open. Same save/restore as ReferAFriendSheet.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Portal to body so position:fixed anchors to the viewport — the subscription
  // flow panel keeps a CSS transform, which would otherwise re-base fixed
  // positioning to the panel instead of the viewport.
  return createPortal(
    <div className={classNames('BottomSheetModal', className)}>
      <div
        className="BottomSheetModal__backdrop"
        onClick={onClose}
        role="button"
        tabIndex={0}
        aria-label="Close"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClose();
          }
        }}
      />
      <div
        className="BottomSheetModal__content"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <span className="BottomSheetModal__handle" aria-hidden="true" />
        {!hideCloseButton && (
          <button
            type="button"
            className="BottomSheetModal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default BottomSheetModal;
